import * as THREE from 'three';
import type { ModelPack, PackModel } from '../../render/models/modelLibrary';
import { emitStreetModel } from '../../render/models/streetModels';
import type { Decor, PropDecor, WallDecor } from '../decor';
import type { RoofKit } from './buildings';
import { ChunkedBuilders, hash01, type MeshBuilder, type RGB } from './meshBuilder';
import { PAVE_Y } from './ground';
import { lampKind } from './lampsView';
import type { StreetLifeKit } from './streetLife';
import { T, tileUV, type SignAtlas } from './textures';

/**
 * Street furniture and small structures, merged per chunk into the shared `props` (painted),
 * `metal`, `foliage` and `glow` builders.
 */

const WHITE = tileUV(T.white, 40);
const IRON: RGB = [0.07, 0.08, 0.08];
const GREEN_IRON: RGB = [0.09, 0.17, 0.13];
const STONE: RGB = [0.6, 0.58, 0.53];

class Local {
  readonly cs: number;
  readonly sn: number;
  constructor(
    readonly x: number,
    readonly z: number,
    heading: number,
    readonly y = PAVE_Y,
  ) {
    this.cs = Math.cos(heading);
    this.sn = Math.sin(heading);
  }
  p(lx: number, ly: number, lz: number): [number, number, number] {
    return [this.x + lx * this.cs + lz * this.sn, this.y + ly, this.z - lx * this.sn + lz * this.cs];
  }
  /** Anything standing on the pavement gets baked contact shading at its foot (docs/POLISH.md, "Graphics batch 2"). */
  box(b: MeshBuilder, lx: number, ly: number, lz: number, sx: number, sy: number, sz: number, c: RGB, heading: number, uv = WHITE): void {
    const p = this.p(lx, ly, lz);
    b.box(p[0], p[1], p[2], sx, sy, sz, heading, c, uv, { baseShade: ly < 0.01 ? PROP_FOOT : 1 });
  }
  cyl(b: MeshBuilder, lx: number, lz: number, y0: number, y1: number, r0: number, r1: number, segs: number, c: RGB, top = true): void {
    const p = this.p(lx, 0, lz);
    b.cylinder(p[0], p[2], this.y + y0, this.y + y1, r0, r1, segs, c, WHITE, { top, baseShade: y0 < 0.01 ? PROP_FOOT : 1 });
  }
}

/** Contact shading at the foot of props, walls and tree roots (local occlusion, not sunlight). */
const PROP_FOOT = 0.72;
const ROOT_FOOT = 0.55;

const foliageGeo = new THREE.IcosahedronGeometry(1, 2);
const foliageLo = new THREE.IcosahedronGeometry(1, 1);
const lumpGeo = new THREE.IcosahedronGeometry(1, 0);
const coneGeo = new THREE.ConeGeometry(1, 1, 8, 1);
const m4 = new THREE.Matrix4();
const q = new THREE.Quaternion();
const s3 = new THREE.Vector3();
const p3 = new THREE.Vector3();

function blob(b: MeshBuilder, x: number, y: number, z: number, sx: number, sy: number, sz: number, c: RGB, rot = 0, geo: THREE.BufferGeometry = foliageGeo): void {
  q.setFromEuler(new THREE.Euler(rot * 0.3, rot, rot * 0.2));
  m4.compose(p3.set(x, y, z), q, s3.set(sx, sy, sz));
  b.geometry(geo, m4, c, WHITE);
}

/**
 * The season (it is October): most trees still in leaf, a third turning, a few gone copper. Picked
 * per tree from its position, so the same tree is the same colour every visit.
 */
export function treePalette(h: number): { leaf: RGB; shade: RGB; crown: RGB } {
  if (h < 0.55) return { leaf: [0.08, 0.14, 0.06], shade: [0.05, 0.09, 0.04], crown: [0.11, 0.17, 0.07] };
  if (h < 0.85) return { leaf: [0.15, 0.15, 0.05], shade: [0.09, 0.1, 0.04], crown: [0.2, 0.18, 0.06] };
  return { leaf: [0.22, 0.1, 0.03], shade: [0.13, 0.06, 0.02], crown: [0.27, 0.13, 0.04] };
}

/**
 * The Meshy street models that replace whole prop families (docs/research/MESHY-WORLD-PLAN.md §4: a family is
 * replaced district-wide, never one beside a procedural twin). Only families whose model is in the pack.
 */
export interface StreetModelSet {
  atlas: THREE.Texture | null;
  /** Plane tree variants (one or two), picked per tree from its position. */
  trees: PackModel[];
  bandstand: PackModel | null;
  phoneBox: PackModel | null;
  /** Tier 2: the pillar box (the red iron set with the phone box), the park bench and the war memorial. */
  pillarBox: PackModel | null;
  bench: PackModel | null;
  memorial: PackModel | null;
  /** The bus shelter (streetLife's busStops; the flag pole stays procedural). */
  busShelter: PackModel | null;
  /**
   * The Blender street kit (art/models/blender/export_street.py): the lamp columns (concrete on the sodium-lit
   * roads, cast iron on the old streets; lampsView draws the lantern either way) and the litter bin on one in three.
   */
  lampConcrete: PackModel | null;
  lampIron: PackModel | null;
  litterBin: PackModel | null;
  /** The roof kit (buildings.ts: chimney stacks, water tanks, ventilator cowls), or null without any of it. */
  roofKit: RoofKit | null;
  /**
   * The street-life kit (art/models/blender/export_street_life.py): the Belisha post, the oil drum and the tyre
   * of the yard props, the railing / palisade panels tiled along walls; the rest (bikes, prams, the bus-stop
   * post, the moored boats, crates, washing poles) is streetLife's, in `life`.
   */
  belisha: PackModel | null;
  oilDrum: PackModel | null;
  tyre: PackModel | null;
  railingPanel: PackModel | null;
  palisadePanel: PackModel | null;
  life: StreetLifeKit;
}

export function streetModelSet(pack: ModelPack | null): StreetModelSet | null {
  if (!pack) return null;
  const trees = ['tree-a', 'tree-b'].map((id) => pack.get(id)).filter((m): m is PackModel => !!m);
  const set: StreetModelSet = {
    atlas: pack.textures[0] ?? null,
    trees,
    bandstand: pack.get('bandstand'),
    phoneBox: pack.get('phone-box'),
    pillarBox: pack.get('pillar-box'),
    bench: pack.get('bench'),
    memorial: pack.get('war-memorial'),
    busShelter: pack.get('bus-shelter'),
    lampConcrete: pack.get('lamp-concrete'),
    lampIron: pack.get('lamp-iron'),
    litterBin: pack.get('litter-bin'),
    roofKit: null,
    belisha: pack.get('belisha'),
    oilDrum: pack.get('oil-drum'),
    tyre: pack.get('tyre'),
    railingPanel: pack.get('railing-panel'),
    palisadePanel: pack.get('palisade-panel'),
    life: {
      shelter: pack.get('bus-shelter'),
      bicycle: pack.get('bicycle'),
      pram: pack.get('pram'),
      busStop: pack.get('bus-stop'),
      lighter: pack.get('lighter'),
      narrowboat: pack.get('narrowboat'),
      milkCrate: pack.get('milk-crate'),
      washingPole: pack.get('washing-pole'),
    },
  };
  const kit: RoofKit = {
    chimneyA: pack.get('chimney-a'),
    chimneyB: pack.get('chimney-b'),
    waterTank: pack.get('water-tank'),
    vent: pack.get('vent-cowl'),
    ventStack: pack.get('roof-vent-stack'),
    skylight: pack.get('roof-skylight'),
    aerial: pack.get('roof-aerial'),
    dormer: pack.get('roof-dormer'),
    house: pack.get('roof-house'),
  };
  if (Object.values(kit).some(Boolean)) set.roofKit = kit;
  const life = Object.values(set.life).some(Boolean) || set.belisha || set.oilDrum || set.tyre || set.railingPanel || set.palisadePanel;
  return set.atlas && (trees.length || set.bandstand || set.phoneBox || set.pillarBox || set.bench || set.memorial || set.busShelter || set.lampConcrete || set.lampIron || set.roofKit || life) ? set : null;
}

/** Which lamp column a lamp gets: the 1960s concrete column where the lighting is new (sodium), cast iron elsewhere. */
function lampColumn(pr: PropDecor, models: StreetModelSet): PackModel | null {
  const kind = lampKind({ pos: pr.pos, heading: pr.heading, type: 'lamp' });
  const concrete = kind === 'sodium' || kind === 'lpSodium';
  return (concrete ? models.lampConcrete : models.lampIron) ?? models.lampConcrete ?? models.lampIron;
}

/** The Meshy plane's bark is pale cream: darkened toward the procedural trunk so it does not read as a white stick from above. */
const TRUNK: RGB = [0.5, 0.42, 0.36];
/** The procedural tree's crown colour (the green palette) — the reference the per-tree tint is taken against. */
const GREEN = treePalette(0).leaf;
const _mm = new THREE.Matrix4();
const _mq = new THREE.Quaternion();
const _ms = new THREE.Vector3();
const _mp = new THREE.Vector3();
const UP = new THREE.Vector3(0, 1, 0);

function placeModel(x: number, y: number, z: number, yaw: number, scale: number): THREE.Matrix4 {
  _mq.setFromAxisAngle(UP, yaw);
  return _mm.compose(_mp.set(x, y, z), _mq, _ms.setScalar(scale));
}

/** As `placeModel` with a scale per axis (a wall panel stretched to its bay, a lighter to its length). */
export function placeModelXYZ(x: number, y: number, z: number, yaw: number, sx: number, sy: number, sz: number): THREE.Matrix4 {
  _mq.setFromAxisAngle(UP, yaw);
  return _mm.compose(_mp.set(x, y, z), _mq, _ms.set(sx, sy, sz));
}

/**
 * The tint that turns a grey vertex-baked model into a paint: the street-life models that come in colours (the oil
 * drum, the milk crate, the palisade) are baked at `albedo` sRGB and tinted by the game. 0.42 sRGB is 0.147 linear.
 */
export function paintTint(paint: RGB, albedo = 0.147): RGB {
  return [paint[0] / albedo, paint[1] / albedo, paint[2] / albedo];
}

export function buildProps(decor: Decor, cb: ChunkedBuilders, signs: SignAtlas, models: StreetModelSet | null = null): void {
  for (const pr of decor.props) emitProp(pr, cb, signs, models);
  for (const w of decor.walls) emitWall(w, cb, models);
}

/** Emit one prop. With `models`, a family that has a street model emits it (MEDIUM / HIGH) and its procedural self as the LOW stand-in. */
export function emitProp(pr: PropDecor, cb: ChunkedBuilders, signs: SignAtlas, models: StreetModelSet | null = null): void {
  const v = hash01(Math.round(pr.pos.x * 10), Math.round(pr.pos.z * 10));
  const model =
    !models ? null
    : pr.type === 'tree' && models.trees.length ? models.trees[Math.floor(hash01(Math.round(pr.pos.x * 10), Math.round(pr.pos.z * 10), 31) * models.trees.length)]!
    : pr.type === 'bandstand' ? models.bandstand
    : pr.type === 'phoneBox' ? models.phoneBox
    : pr.type === 'pillarBox' ? models.pillarBox
    : pr.type === 'bench' ? models.bench
    : pr.type === 'memorial' ? models.memorial
    : pr.type === 'lamp' ? lampColumn(pr, models)
    : pr.type === 'belisha' ? models.belisha
    : pr.type === 'drums' ? models.oilDrum
    : pr.type === 'tyres' ? models.tyre
    : null;
  const key = (k: string): string => (model ? `${k}Low` : k);
  const props = cb.get(key('props'), pr.pos.x, pr.pos.z);
  const metal = cb.get(key('metal'), pr.pos.x, pr.pos.z);
  const glow = cb.get(key('glow'), pr.pos.x, pr.pos.z);
  const fol = cb.get(key('foliage'), pr.pos.x, pr.pos.z);
  const L = new Local(pr.pos.x, pr.pos.z, pr.heading);
  const h = pr.heading;
  if (model) emitPropModel(pr, model, cb, v, models);
  switch (pr.type) {
    case 'lamp': {
      // Cast-iron column with a swan-neck arm and lantern (heading points at the road).
      L.cyl(metal, 0, 0, 0, 0.5, 0.17, 0.13, 8, IRON);
      L.cyl(metal, 0, 0, 0.5, 6.4, 0.08, 0.06, 6, IRON, false);
      L.box(metal, 0, 6.3, 0.6, 0.07, 0.07, 1.3, IRON, h);
      L.box(metal, 0, 6.05, 1.25, 0.4, 0.25, 0.4, IRON, h);
      // Corporation litter bin strapped to every third post (rides on the post's collider)
      if (v < 0.34) L.cyl(props, 0, -0.24, 0.75, 1.3, 0.2, 0.22, 8, [0.14, 0.2, 0.16]);
      // a fly-poster wrapped round the column on a few
      else if (v > 0.9) L.cyl(props, 0, 0, 1.4, 2.1, 0.095, 0.095, 6, [0.8, 0.74, 0.6], false);
      break;
    }
    case 'bridgeLamp': {
      L.cyl(props, 0, 0, 0, 1.0, 0.3, 0.24, 8, GREEN_IRON);
      L.cyl(metal, 0, 0, 1.0, 4.6, 0.1, 0.08, 6, GREEN_IRON, false);
      L.box(metal, 0, 4.5, 0, 1.6, 0.08, 0.08, GREEN_IRON, h);
      for (const sx of [-0.8, 0.8]) {
        L.box(metal, sx, 4.15, 0, 0.36, 0.42, 0.36, GREEN_IRON, h);
      }
      break;
    }
    case 'wallLamp': {
      L.box(metal, 0, 3.6, 0.3, 0.06, 0.06, 0.6, IRON, h);
      L.box(metal, 0, 3.25, 0.6, 0.32, 0.38, 0.32, IRON, h);
      L.box(glow, 0, 3.3, 0.6, 0.24, 0.26, 0.24, [3.2, 1.9, 0.8], h);
      break;
    }
    case 'bollard':
      L.cyl(metal, 0, 0, 0, 0.95, 0.13, 0.11, 8, IRON);
      break;
    case 'mooring':
      L.cyl(metal, 0, 0, 0, 0.45, 0.22, 0.17, 8, IRON, false);
      L.cyl(metal, 0, 0, 0.45, 0.6, 0.3, 0.3, 8, IRON);
      break;
    case 'pillarBox': {
      const red: RGB = [0.42, 0.05, 0.05];
      L.cyl(props, 0, 0, 0, 1.25, 0.32, 0.32, 10, red);
      L.cyl(props, 0, 0, 1.25, 1.5, 0.36, 0.2, 10, red);
      L.box(props, 0, 0.98, 0.31, 0.3, 0.05, 0.04, IRON, h);
      L.cyl(props, 0, 0, 1.08, 1.16, 0.335, 0.335, 10, [0.75, 0.6, 0.25], false);
      break;
    }
    case 'phoneBox': {
      // Post Office kiosk: pillar-box red cast iron, glazing-bar panes, lit TELEPHONE band, domed roof.
      const red: RGB = [0.46, 0.05, 0.04];
      L.box(props, 0, 0, 0, 1.05, 0.12, 1.05, STONE, h);
      for (const [cx, cz] of [
        [-0.47, -0.47],
        [0.47, -0.47],
        [0.47, 0.47],
        [-0.47, 0.47],
      ] as const) {
        L.box(props, cx, 0.12, cz, 0.13, 2.25, 0.13, red, h);
      }
      L.box(glow, 0, 0.75, 0, 0.86, 1.3, 0.86, [1.6, 1.45, 1.0], h);
      // glazing bars over the lit panes (three rows a side)
      for (const yb of [1.1, 1.45, 1.8]) L.box(props, 0, yb, 0, 0.9, 0.04, 0.9, red, h);
      L.box(props, 0, 0.12, 0, 0.9, 0.6, 0.9, red, h);
      L.box(props, 0, 2.05, 0, 0.94, 0.32, 0.94, red, h);
      L.box(glow, 0, 2.1, 0, 0.98, 0.18, 0.98, [2.6, 2.4, 2.0], h);
      L.box(props, 0, 2.37, 0, 1.1, 0.12, 1.1, red, h);
      L.cyl(props, 0, 0, 2.49, 2.72, 0.62, 0.32, 8, red);
      break;
    }
    case 'bench': {
      const wood: RGB = [0.36, 0.24, 0.15];
      L.box(props, 0, 0.42, 0, 1.8, 0.05, 0.45, wood, h);
      L.box(props, 0, 0.55, -0.24, 1.8, 0.4, 0.05, wood, h);
      for (const sx of [-0.8, 0.8]) L.box(metal, sx, 0, 0, 0.06, 0.45, 0.5, IRON, h);
      break;
    }
    case 'tree': {
      // A London plane: a leaning trunk that forks into three limbs, a crown built from a dark
      // under-layer of coarse blobs, a mid ring and a lit top, in the season's colour. The limbs
      // show between the blobs from the high camera, which is what stops it reading as one lump.
      const s = pr.scale ?? 1;
      const pal = treePalette(hash01(Math.round(pr.pos.x * 10), Math.round(pr.pos.z * 10), 77));
      const bark: RGB = [0.3, 0.27, 0.22];
      const lean = (v - 0.5) * 0.5 * s;
      const base = L.p(0, 0, 0);
      props.cylinder(base[0], base[2], base[1], base[1] + 1.2 * s, 0.34 * s, 0.26 * s, 7, [0.26, 0.23, 0.19], WHITE, { top: false, baseShade: ROOT_FOOT });
      props.cylinder(base[0] + lean * 0.5, base[2], base[1] + 1.2 * s, base[1] + 3.4 * s, 0.26 * s, 0.17 * s, 7, bark, WHITE, { top: false });
      const fork: [number, number, number] = [base[0] + lean, base[1] + 3.3 * s, base[2]];
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2 + v * 4;
        props.beam(fork, [fork[0] + Math.cos(a) * 1.6 * s, fork[1] + 2.1 * s, fork[2] + Math.sin(a) * 1.6 * s], 0.14 * s, bark, WHITE);
      }
      const n = 5;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + v * 3;
        const r = (1.7 + hash01(i, Math.round(v * 1000)) * 0.9) * s;
        const sh = 0.9 + hash01(i, 3, Math.round(v * 1000)) * 0.25;
        blob(fol, fork[0] + Math.cos(a) * r, base[1] + (4.7 + hash01(i, 9) * 0.6) * s, fork[2] + Math.sin(a) * r, 2.1 * s, 1.5 * s, 2.1 * s, [pal.shade[0] * sh, pal.shade[1] * sh, pal.shade[2] * sh], a, foliageLo);
      }
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + v * 5 + 0.7;
        const r = (1.1 + hash01(i, 5, Math.round(v * 1000)) * 0.7) * s;
        const sh = 0.9 + hash01(i, 6, Math.round(v * 1000)) * 0.25;
        blob(fol, fork[0] + Math.cos(a) * r, base[1] + (6.0 + hash01(i, 7) * 0.8) * s, fork[2] + Math.sin(a) * r, 2.0 * s, 1.6 * s, 2.0 * s, [pal.leaf[0] * sh, pal.leaf[1] * sh, pal.leaf[2] * sh], a);
      }
      blob(fol, fork[0] + lean * 0.3, base[1] + 7.3 * s, fork[2], 2.3 * s, 1.7 * s, 2.3 * s, pal.crown, v * 6);
      break;
    }
    case 'yew': {
      const s = pr.scale ?? 1;
      const base = L.p(0, 0, 0);
      L.cyl(props, 0, 0, 0, 1.2, 0.2, 0.15, 6, [0.25, 0.18, 0.14]);
      q.identity();
      m4.compose(p3.set(base[0], base[1] + 3.2 * s, base[2]), q, s3.set(1.9 * s, 5.0 * s, 1.9 * s));
      fol.geometry(coneGeo, m4, [0.06, 0.12, 0.08], WHITE);
      blob(fol, base[0], base[1] + 1.8 * s, base[2], 1.8 * s, 1.4 * s, 1.8 * s, [0.07, 0.13, 0.08], v);
      break;
    }
    case 'grave': {
      const c: RGB = [0.3 + v * 0.1, 0.3 + v * 0.1, 0.29 + v * 0.08];
      const variant = pr.variant ?? 0;
      if (variant === 0) L.box(props, 0, 0, 0, 0.7, 0.9, 0.12, c, h);
      else if (variant === 1) {
        L.box(props, 0, 0, 0, 0.12, 1.1, 0.12, c, h);
        L.box(props, 0, 0.65, 0, 0.55, 0.12, 0.12, c, h);
      } else if (variant === 2) L.box(props, 0, 0, 0.6, 0.8, 0.35, 1.8, c, h);
      else L.box(props, 0, 0, 0, 0.6, 0.65, 0.15, c, h);
      break;
    }
    case 'belisha': {
      for (let k = 0; k < 6; k++) L.cyl(props, 0, 0, k * 0.42, (k + 1) * 0.42, 0.06, 0.06, 6, k % 2 === 0 ? [0.08, 0.08, 0.08] : [0.85, 0.85, 0.82], false);
      // the globe itself is added to the flashing beacon mesh by the city view
      break;
    }
    case 'drums': {
      const cols: RGB[] = [
        [0.5, 0.12, 0.08],
        [0.15, 0.25, 0.4],
        [0.3, 0.32, 0.2],
        [0.55, 0.4, 0.1],
      ];
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2;
        const r = i === 0 ? 0 : 0.75;
        L.cyl(props, Math.cos(a) * r, Math.sin(a) * r, 0, 0.9, 0.29, 0.29, 8, cols[(i + Math.floor(v * 4)) % 4]!);
      }
      L.cyl(props, 0.3, 0.2, 0.9, 1.8, 0.29, 0.29, 8, cols[1]!);
      break;
    }
    case 'tyres': {
      for (let i = 0; i < 6; i++) {
        const ox = (i % 3) * 0.7 - 0.7;
        const oz = Math.floor(i / 3) * 0.7 - 0.35;
        const n = 2 + Math.floor(hash01(i, Math.round(v * 99)) * 4);
        L.cyl(props, ox, oz, 0, n * 0.22, 0.34, 0.34, 8, [0.05, 0.05, 0.05]);
      }
      break;
    }
    case 'bandstand': {
      const cream: RGB = [0.8, 0.76, 0.66];
      const copper: RGB = [0.2, 0.42, 0.36];
      L.cyl(props, 0, 0, 0, 0.9, 4.4, 4.4, 8, STONE);
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        L.cyl(metal, Math.cos(a) * 4.0, Math.sin(a) * 4.0, 0.9, 4.2, 0.09, 0.09, 6, GREEN_IRON, false);
      }
      L.cyl(props, 0, 0, 4.2, 4.5, 4.6, 4.6, 8, cream);
      L.cyl(props, 0, 0, 4.5, 6.6, 4.7, 0.3, 8, copper);
      L.cyl(props, 0, 0, 6.6, 7.4, 0.08, 0.02, 4, [0.75, 0.6, 0.25]);
      // festoon of lamps around the eaves
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2;
        const p = L.p(Math.cos(a) * 4.62, 4.25, Math.sin(a) * 4.62);
        glow.box(p[0], p[1], p[2], 0.14, 0.14, 0.14, 0, [3.2, 2.4, 1.3], WHITE);
      }
      break;
    }
    case 'memorial': {
      L.box(props, 0, 0, 0, 2.4, 0.5, 2.4, STONE, h);
      L.box(props, 0, 0.5, 0, 1.6, 0.6, 1.6, STONE, h);
      L.box(props, 0, 1.1, 0, 0.9, 3.4, 0.9, [0.66, 0.64, 0.59], h);
      L.cyl(props, 0, 0, 4.5, 5.6, 0.45, 0.02, 4, [0.66, 0.64, 0.59]);
      L.box(props, 0, 1.8, 0.47, 0.6, 0.9, 0.04, [0.2, 0.3, 0.26], h);
      void signs;
      break;
    }
    default:
      break;
  }
}

/** Festoon lamps round the Meshy bandstand's eaves: ring radius and height as fractions of the model's size. */
const BANDSTAND_FESTOON = { r: 0.48, y: 0.58 };
/** The yard drums' paints (the procedural drums' colours, linear). */
const DRUM_PAINTS: RGB[] = [
  [0.5, 0.12, 0.08],
  [0.15, 0.25, 0.4],
  [0.3, 0.32, 0.2],
  [0.55, 0.4, 0.1],
];

/** A street model in place of a procedural prop (into the `…Model` keys: MEDIUM and HIGH only). */
function emitPropModel(pr: PropDecor, model: PackModel, cb: ChunkedBuilders, v: number, models: StreetModelSet | null): void {
  const { x, z } = pr.pos;
  switch (pr.type) {
    case 'tree': {
      // Random yaw and a little size spread, so two variants never read as copies; the season tint goes on the
      // leaves only (the model's tint weight), relative to the green palette, at 60 % so the texture still reads.
      const s = (pr.scale ?? 1) * (0.92 + hash01(Math.round(x * 10), Math.round(z * 10), 53) * 0.16);
      const pal = treePalette(hash01(Math.round(x * 10), Math.round(z * 10), 77)).leaf;
      const tint: RGB = [1 + (pal[0] / GREEN[0] - 1) * 0.6, 1 + (pal[1] / GREEN[1] - 1) * 0.6, 1 + (pal[2] / GREEN[2] - 1) * 0.6];
      emitStreetModel(cb.get('foliageModel', x, z), model, placeModel(x, PAVE_Y, z, v * Math.PI * 2, s), { tint, untinted: TRUNK, foot: ROOT_FOOT, footHeight: 0.9 });
      break;
    }
    case 'phoneBox': {
      // the Blender K6 is baked to vertex colours with its panes open: the lit panes and the TELEPHONE band are
      // glow boxes inside it, as the procedural kiosk's (the glow builder is emissive)
      emitStreetModel(cb.get('propsModel', x, z), model, placeModel(x, PAVE_Y, z, pr.heading, 1), { foot: PROP_FOOT, footHeight: 0.4 });
      const glow = cb.get('glowModel', x, z);
      const L = new Local(x, z, pr.heading);
      // (dimmer than the procedural kiosk's panes: the open K6 shows the whole box, and at full strength it
      // bloomed into one white block at night)
      L.box(glow, 0, 0.78, 0, 0.84, 1.32, 0.84, [0.6, 0.5, 0.3], pr.heading);
      L.box(glow, 0, 2.14, 0, 0.96, 0.24, 0.96, [1.4, 1.2, 0.9], pr.heading);
      break;
    }
    case 'lamp': {
      // the column (the lantern head and its halo are lampsView's instanced meshes, 1.25 m along the heading at
      // 5.96 m: the bracket ends there); the litter bin on one column in three, the fly-poster on a few
      emitStreetModel(cb.get('propsModel', x, z), model, placeModel(x, PAVE_Y, z, pr.heading, 1), { foot: PROP_FOOT, footHeight: 0.5 });
      const L = new Local(x, z, pr.heading);
      if (v < 0.34 && models?.litterBin) {
        const p = L.p(0, 0, -0.27);
        emitStreetModel(cb.get('propsModel', x, z), models.litterBin, placeModel(p[0], PAVE_Y + 0.72, p[2], pr.heading, 1));
      } else if (v > 0.9) L.cyl(cb.get('clutter', x, z), 0, 0, 1.4, 2.1, 0.1, 0.1, 6, [0.8, 0.74, 0.6], false);
      break;
    }
    case 'pillarBox':
    case 'bench':
      emitStreetModel(cb.get('propsModel', x, z), model, placeModel(x, PAVE_Y, z, pr.heading, 1), { foot: PROP_FOOT, footHeight: 0.4 });
      break;
    case 'belisha':
      // the banded post; the flashing globe stays the city view's `beacons` mesh on top of it
      emitStreetModel(cb.get('propsModel', x, z), model, placeModel(x, PAVE_Y, z, pr.heading, 1), { foot: PROP_FOOT, footHeight: 0.3 });
      break;
    case 'drums': {
      // the same ring of seven and one on top as the procedural drums, each tinted to its paint
      const b = cb.get('propsModel', x, z);
      const L = new Local(x, z, pr.heading);
      const put = (lx: number, lz: number, y: number, c: RGB, k: number): void => {
        const p = L.p(lx, 0, lz);
        const tint = paintTint(c);
        emitStreetModel(b, model, placeModel(p[0], PAVE_Y + y, p[2], pr.heading + k * 0.9, 1), { tint, untinted: tint, foot: y > 0 ? 1 : PROP_FOOT, footHeight: 0.25 });
      };
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2;
        const r = i === 0 ? 0 : 0.75;
        put(Math.cos(a) * r, Math.sin(a) * r, 0, DRUM_PAINTS[(i + Math.floor(v * 4)) % 4]!, i);
      }
      put(0.3, 0.2, 0.9, DRUM_PAINTS[1]!, 7);
      break;
    }
    case 'tyres': {
      // six stacks of two to five tyres, each tyre turned a little and off by a few centimetres: a heap, not a column
      const b = cb.get('propsModel', x, z);
      const L = new Local(x, z, pr.heading);
      for (let i = 0; i < 6; i++) {
        const ox = (i % 3) * 0.7 - 0.7;
        const oz = Math.floor(i / 3) * 0.7 - 0.35;
        const n = 2 + Math.floor(hash01(i, Math.round(v * 99)) * 4);
        for (let k = 0; k < n; k++) {
          const j = hash01(i, k, Math.round(v * 99));
          const p = L.p(ox + (j - 0.5) * 0.06, 0, oz + (hash01(k, i, 5) - 0.5) * 0.06);
          emitStreetModel(b, model, placeModel(p[0], PAVE_Y + k * model.height, p[2], j * 6.28, 1), { foot: k === 0 ? PROP_FOOT : 1, footHeight: 0.1 });
        }
      }
      break;
    }
    case 'memorial':
      emitStreetModel(cb.get('propsModel', x, z), model, placeModel(x, PAVE_Y, z, pr.heading, 1), { foot: PROP_FOOT, footHeight: 0.6 });
      break;
    case 'bandstand': {
      emitStreetModel(cb.get('propsModel', x, z), model, placeModel(x, PAVE_Y, z, pr.heading, 1), { foot: PROP_FOOT, footHeight: 0.5 });
      const glow = cb.get('glowModel', x, z);
      const L = new Local(x, z, pr.heading);
      const r = model.radius * BANDSTAND_FESTOON.r * 2;
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2;
        const p = L.p(Math.cos(a) * r, model.height * BANDSTAND_FESTOON.y, Math.sin(a) * r);
        glow.box(p[0], p[1], p[2], 0.14, 0.14, 0.14, 0, [3.2, 2.4, 1.3], WHITE);
      }
      break;
    }
    default:
      break;
  }
}

/** The panel models' natural sizes (export_street_life.py): 1.6 m bays, railings 1.2 m to the top rail, palisade 2.0 m. */
const PANEL = { bay: 1.6, railingH: 1.2, palisadeH: 2.0 };

function emitWall(w: WallDecor, cb: ChunkedBuilders, models: StreetModelSet | null = null): void {
  const len = Math.hypot(w.b.x - w.a.x, w.b.z - w.a.z);
  if (len < 0.05) return;
  const dx = (w.b.x - w.a.x) / len;
  const dz = (w.b.z - w.a.z) / len;
  const heading = Math.atan2(dx, dz);
  const midx = (w.a.x + w.b.x) / 2;
  const midz = (w.a.z + w.b.z) / 2;
  const y = PAVE_Y;
  // a railing / palisade panel model (MEDIUM / HIGH) tiled along the run and stretched to its bay and height;
  // the crossed quads below become its LOW stand-in
  const panel = w.kind === 'railing' ? models?.railingPanel : w.kind === 'palisade' ? models?.palisadePanel : null;
  if (panel) {
    const b = cb.get('propsModel', midx, midz);
    const n = Math.max(1, Math.round(len / PANEL.bay));
    const bay = len / n;
    const sy = w.height / (w.kind === 'railing' ? PANEL.railingH : PANEL.palisadeH);
    // the model's x runs along the wall: a yaw that turns local +x onto a -> b
    const yaw = Math.atan2(-dz, dx);
    const tint = w.kind === 'palisade' ? paintTint(GREEN_IRON) : null;
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      const m = placeModelXYZ(w.a.x + (w.b.x - w.a.x) * t, y, w.a.z + (w.b.z - w.a.z) * t, yaw, bay / PANEL.bay, sy, 1);
      emitStreetModel(b, panel, m, { foot: PROP_FOOT, footHeight: 0.3, ...(tint ? { tint, untinted: tint } : {}) });
    }
  }
  switch (w.kind) {
    case 'brick':
    case 'gardenWall':
    case 'parapet': {
      const fb = cb.get('facade', midx, midz);
      const th = w.kind === 'gardenWall' ? 0.25 : w.kind === 'parapet' ? 0.5 : 0.35;
      const tile = w.kind === 'parapet' ? T.stonePlain : T.brickPlain;
      const tint: RGB = w.kind === 'parapet' ? [0.95, 0.93, 0.88] : [0.85, 0.8, 0.78];
      const n = Math.max(1, Math.round(len / 3.5));
      for (let i = 0; i < n; i++) {
        const t = (i + 0.5) / n;
        fb.box(w.a.x + (w.b.x - w.a.x) * t, y, w.a.z + (w.b.z - w.a.z) * t, th, w.height, len / n + 0.01, heading, tint, tileUV(tile), { uvTop: WHITE, topColour: [0.26, 0.25, 0.24], baseShade: PROP_FOOT });
      }
      // coping
      fb.box(midx, y + w.height, midz, th + 0.12, 0.1, len, heading, [0.27, 0.26, 0.25], WHITE);
      if (w.kind === 'gardenWall') {
        // a privet hedge behind about half the front garden walls: a clipped box (HIGH and MEDIUM)
        // with a lumpy top (HIGH), each run its own height and green
        const hh = hash01(Math.round(w.a.x * 5), Math.round(w.a.z * 5), 88);
        if (hh < 0.5) {
          const hedge = cb.get('foliageMid', midx, midz);
          const lumps = cb.get('foliageFine', midx, midz);
          const tall = 0.75 + hh * 1.0;
          const green: RGB = hh < 0.25 ? [0.06, 0.11, 0.045] : [0.08, 0.13, 0.05];
          // the garden side is to the left of a->b (the wall stands 0.8 m in front of the house)
          const gx = dz * 0.5;
          const gz = -dx * 0.5;
          const segs = Math.max(1, Math.round(len / 3));
          for (let i = 0; i < segs; i++) {
            const t = (i + 0.5) / segs;
            const sh = 0.9 + hash01(i, Math.round(w.a.x * 5), 89) * 0.25;
            hedge.box(w.a.x + (w.b.x - w.a.x) * t + gx, y, w.a.z + (w.b.z - w.a.z) * t + gz, 0.6, tall * sh, len / segs - 0.05, heading, [green[0] * sh, green[1] * sh, green[2] * sh], WHITE, { baseShade: 0.7 });
          }
          for (let t = 0.4; t < len - 0.3; t += 0.95) {
            const sh = 0.95 + hash01(Math.round(t * 10), Math.round(w.a.z * 5), 90) * 0.3;
            const x = w.a.x + dx * t + gx;
            const z = w.a.z + dz * t + gz;
            blob(lumps, x, y + tall * 0.95, z, 0.42, 0.3, 0.42, [green[0] * sh, green[1] * sh, green[2] * sh], t, lumpGeo);
          }
        }
      }
      break;
    }
    case 'corrugated': {
      const fb = cb.get('facade', midx, midz);
      const n = Math.max(1, Math.round(len / 3));
      const tint: RGB = [0.72, 0.62, 0.55];
      for (let i = 0; i < n; i++) {
        const t = (i + 0.5) / n;
        fb.box(w.a.x + (w.b.x - w.a.x) * t, y, w.a.z + (w.b.z - w.a.z) * t, 0.12, w.height, len / n + 0.01, heading, tint, tileUV(T.corrugated), { baseShade: PROP_FOOT });
      }
      break;
    }
    case 'railing':
    case 'palisade': {
      const mb = cb.get(panel ? 'metalLow' : 'metal', midx, midz);
      const col = w.kind === 'railing' ? IRON : GREEN_IRON;
      const hgt = w.height;
      // top & bottom rails
      mb.box(midx, y + hgt - 0.1, midz, 0.06, 0.06, len, heading, col, WHITE);
      mb.box(midx, y + 0.12, midz, 0.06, 0.06, len, heading, col, WHITE);
      const step = w.kind === 'railing' ? 0.32 : 0.22;
      const n = Math.floor(len / step);
      for (let i = 0; i <= n; i++) {
        const t = n === 0 ? 0.5 : i / n;
        const x = w.a.x + (w.b.x - w.a.x) * t;
        const z = w.a.z + (w.b.z - w.a.z) * t;
        // two crossed quads per bar (cheap)
        const r = 0.025;
        mb.quad([x - dz * r, y, z + dx * r], [x + dz * r, y, z - dx * r], [x + dz * r, y + hgt + 0.08, z - dx * r], [x - dz * r, y + hgt + 0.08, z + dx * r], WHITE, col);
        mb.quad([x + dz * r, y, z - dx * r], [x - dz * r, y, z + dx * r], [x - dz * r, y + hgt + 0.08, z + dx * r], [x + dz * r, y + hgt + 0.08, z - dx * r], WHITE, col);
      }
      // stone plinth for park/church railings
      if (w.kind === 'railing') mb.box(midx, y, midz, 0.4, 0.1, len, heading, STONE, WHITE);
      break;
    }
    case 'quayEdge': {
      const pb = cb.get('props', midx, midz);
      // One continuous dark bed, with worn stone caps: joints give the long quay a human scale.
      pb.box(midx, y - 0.01, midz, 0.7, w.height, len, heading, [0.19, 0.185, 0.17], WHITE, { topColour: [0.1, 0.095, 0.085] });
      const frame = new Local(midx, midz, heading, y - 0.007 + w.height);
      const count = Math.max(1, Math.round(len / 1.6));
      for (let i = 0; i < count; i++) {
        const z0 = -len / 2 + len * i / count;
        const z1 = -len / 2 + len * (i + 1) / count;
        const v = 0.21 + hash01(Math.round(midx), Math.round(midz), i + 153) * 0.035;
        pb.quadFacing(frame.p(-0.35, 0, z0 + 0.009), frame.p(0.35, 0, z0 + 0.009), frame.p(0.35, 0, z1 - 0.009), frame.p(-0.35, 0, z1 - 0.009), WHITE, [v, v * 0.98, v * 0.91], [0, 1, 0]);
      }
      break;
    }
  }
}
